import { Input, Loading } from '@lib/components'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import { UPDATE_PROJECT } from '@storeOld/actions'
import {
  NotificationType,
  notificationActionCreators,
} from '@storeOld/reducers/notification'
import cx from 'classnames'
import type React from 'react'
import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import Api from '../../../../../../../../api'
import { PROJECT_SHARE_TYPE } from '../ShareOptions/types'
import css from './ProjectPassword.module.scss'

const Icon = () => (
  <svg
    width="16"
    height="17"
    viewBox="0 0 16 17"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12.6006 4.71924C12.6006 4.33838 12.8936 4.01611 13.3037 4.01611C13.6846 4.01611 14.0068 4.33838 14.0068 4.71924V7.64111C14.0068 9.07666 12.835 10.2192 11.4287 10.2192H4.37988L6.1274 11.9668C6.42037 12.2597 6.42037 12.6992 6.1274 12.9629C5.86373 13.2558 5.42428 13.2558 5.1606 12.9629L2.21191 10.0142C1.91895 9.75049 1.91895 9.31104 2.21191 9.04736L5.1606 6.06938C5.42428 5.8057 5.86373 5.8057 6.1274 6.06938C6.42037 6.36234 6.42037 6.8018 6.1274 7.06547L4.37988 8.81299H11.4287C12.0732 8.81299 12.6006 8.28564 12.6006 7.64111V4.71924Z"
      fill="currentColor"
    />
  </svg>
)

const ProjectPassword: React.FC = () => {
  const [value, setValue] = useState('')
  const [success, setSuccess] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const dispatch = useDispatch()

  const id = useSelector(migrationProjectSelector.getProjectId)
  const shareType = useSelector(migrationProjectSelector.getProjectShareType)
  const hasPassword = shareType === PROJECT_SHARE_TYPE.PASSWORD

  const onChange = (string: string) => {
    setIsLoading(false)
    setSuccess(false)
    setValue(string)
  }

  const onSubmit = async () => {
    fetchPassword(value)
      .then(() => {})
      .catch(() => {
        setSuccess(false)
      })
  }

  const fetchPassword = async (password: string | null) => {
    try {
      if (id) {
        const passwordIsEmpty = password === ''

        if (passwordIsEmpty) {
          dispatch(
            notificationActionCreators.setNotification({
              type: NotificationType.PASSWORD_PROTECTED_EMPTY,
            })
          )

          return
        }

        setIsLoading(true)

        const response = await Api.Projects.setProjectPassword({
          password: password as string,
          projectId: id,
        })

        const data = await response.json()

        dispatch({
          type: UPDATE_PROJECT,
          payload: {
            shareType: data.shareType,
            linkPermission: data.linkPermission,
          },
        })

        dispatch(
          notificationActionCreators.setNotification({
            type: NotificationType.ACCESS_SETTINGS_UPDATED,
          })
        )

        setSuccess(true)
      }
    } catch (error: any) {
      console.error(error)

      dispatch(
        notificationActionCreators.setNotification({
          type: NotificationType.PASSWORD_PROTECTED_ERROR,
        })
      )
    }
  }

  return (
    <div className={css.password_wrapper}>
      <Input
        type="normal"
        htmlType="password"
        placeholder={hasPassword ? 'Change password...' : 'Enter password...'}
        value={value}
        onChange={onChange}
        success={success}
        onKeyUp={(e) => {
          if (e.key === 'Enter') {
            onSubmit()
          }
        }}
        buttonRight={
          isLoading ? (
            <Loading color="gray" type="small" variant="tree" />
          ) : (
            <div
              onClick={onSubmit}
              className={cx(css.button, {
                [css.active]: value.length > 0,
              })}
            >
              <Icon />
            </div>
          )
        }
        autoFocus
      />
    </div>
  )
}

export default ProjectPassword
